/**
 * JWT Token Validation Utility
 * Validates license tokens locally without API calls
 * 
 * Note: This is a simplified JWT decoder. For production, consider using a library like 'jwt-decode'
 */

/**
 * Base64 URL decode
 */
function base64UrlDecode(str) {
    // Replace URL-safe characters
    str = str.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    while (str.length % 4) {
        str += '=';
    }
    
    try {
        // Decode base64
        const decoded = atob(str);
        // Convert to JSON
        return JSON.parse(decoded);
    } catch (e) {
        throw new Error('Invalid token format');
    }
}

/**
 * Check whether a license payload authorizes the given device.
 * A token is device-bound if it carries `allowed_devices` (array) and/or a
 * single `device_id`. Tokens without any device binding are treated as
 * authorized for backward compatibility.
 *
 * @param {Object} payload - Decoded token payload
 * @param {string|null} deviceId - Current device id
 * @returns {boolean}
 */
export function isDeviceAuthorized(payload, deviceId) {
    if (!payload) return false;

    const allowed = Array.isArray(payload.allowed_devices) ? payload.allowed_devices : null;
    const single = payload.device_id || null;

    // No device binding in the token -> backward compatible, allow.
    if (!allowed && !single) {
        return true;
    }

    // Token is device-bound but we couldn't resolve a local device id -> deny.
    if (!deviceId) {
        return false;
    }

    if (allowed && allowed.includes(deviceId)) {
        return true;
    }
    if (single && single === deviceId) {
        return true;
    }
    return false;
}

/**
 * Validate JWT token locally
 * Note: This only validates structure and expiration, not signature
 * For full security, signature should be verified server-side
 *
 * @param {string} token - JWT token string
 * @param {string|null} [deviceId] - Current device id for seat binding check
 * @returns {Object} Validation result
 */
export function validateLicenseToken(token, deviceId = null) {
    if (!token || typeof token !== 'string') {
        return {
            valid: false,
            error: 'Invalid token format'
        };
    }

    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return {
                valid: false,
                error: 'Invalid token structure'
            };
        }

        // Decode payload (without signature verification)
        const payload = base64UrlDecode(parts[1]);

        // Check device binding before anything else — a license cached on one
        // machine must not validate when copied to another.
        if (!isDeviceAuthorized(payload, deviceId)) {
            return {
                valid: false,
                payload: payload,
                deviceAuthorized: false,
                error: 'device_not_authorized'
            };
        }

        // Check expiration
        const now = Math.floor(Date.now() / 1000);
        const expiresAt = payload.exp;
        const expired = expiresAt && now > expiresAt;

        // Check grace period if expired
        let inGracePeriod = false;
        if (expired && payload.grace_period_end) {
            const graceEnd = new Date(payload.grace_period_end).getTime() / 1000;
            inGracePeriod = now <= graceEnd;
        }

        return {
            valid: !expired || inGracePeriod,
            payload: payload,
            deviceAuthorized: true,
            expired: expired && !inGracePeriod,
            inGracePeriod: inGracePeriod,
            error: expired && !inGracePeriod ? 'Token expired' : null
        };
    } catch (error) {
        return {
            valid: false,
            error: error.message || 'Token validation failed'
        };
    }
}

/**
 * Extract payload from JWT token (without validation)
 */
export function decodeLicenseToken(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }
        return base64UrlDecode(parts[1]);
    } catch (error) {
        return null;
    }
}

